import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ChevronRight,
  Shield,
  Award,
  PoundSterling,
  DoorOpen,
  Ruler,
  PanelTop,
  Sparkles,
  Phone,
  MapPin,
  CheckCircle,
} from "lucide-react";

const services = [
  {
    icon: DoorOpen,
    title: "Door Hanging",
    description: "Internal doors hung properly across Sale. Doors that fit, close and latch correctly.",
    link: "/doors",
  },
  {
    icon: Ruler,
    title: "Skirting & Architraves",
    description: "Neat fitting of skirting boards and architraves throughout Sale properties.",
    link: "/trimwork",
  },
  {
    icon: PanelTop,
    title: "Wall Panelling",
    description: "Transform rooms with professionally fitted wall panelling. Popular in Sale homes.",
    link: "/panelling",
  },
  {
    icon: Sparkles,
    title: "Floor Sanding",
    description: "Dust-free floor sanding for Sale properties. Restore your wooden floors.",
    link: "/sanding",
  },
];

const areas = [
  "Sale",
  "Sale Moor",
  "Ashton upon Mersey",
  "Brooklands",
  "Timperley",
  "Altrincham",
  "Hale",
  "Bowdon",
  "Stretford",
  "Urmston",
];

const SalePage = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Expert Carpenter Sale",
    "description": "Professional carpentry services in Sale. Door hanging, skirting boards, wall panelling and floor sanding.",
    "url": "https://expert-carpenter.co.uk/sale",
    "telephone": "07300405045",
    "areaServed": {
      "@type": "City",
      "name": "Sale",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Greater Manchester"
      }
    },
    "priceRange": "££",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "08:00",
      "closes": "18:00"
    }
  };

  return (
    <div>
      <Helmet>
        <title>Carpenter in Sale | Door Hanging & Fitting | Expert Carpenter</title>
        <meta name="description" content="Professional carpenter in Sale for door hanging, skirting boards, architraves and wall panelling. 12-month guarantee, no VAT. Free quotes. Call 07300 405045." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/sale" />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-[#C9A227] mb-4">
            <MapPin className="h-5 w-5" />
            <span className="font-semibold">Serving Sale & Surrounding Areas</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Carpenter in <span className="text-[#C9A227]">Sale</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mb-6">
            Professional carpentry services for homes across Sale. Door hanging, trimwork, panelling and floor sanding. Local carpenter you can trust.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-3 mb-8">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Shield className="h-5 w-5 text-[#C9A227]" />
              <span className="text-white text-sm font-medium">12-Month Guarantee</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Award className="h-5 w-5 text-[#C9A227]" />
              <span className="text-white text-sm font-medium">Price Promise</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <PoundSterling className="h-5 w-5 text-[#C9A227]" />
              <span className="text-white text-sm font-medium">No VAT</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold h-12 px-8"
                data-testid="sale-hero-quote-btn"
              >
                Get a Free Quote
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:07300405045">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 font-bold h-12"
              >
                <Phone className="mr-2 h-5 w-5" />
                07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#3A3A3A] mb-4">
              Your Local Sale Carpenter
            </h2>
            <p className="text-gray-600 mb-4 leading-relaxed">
              Looking for a reliable carpenter in Sale? I provide professional carpentry services throughout Sale and the surrounding areas, including Altrincham, Timperley, Hale, and Stretford.
            </p>
            <p className="text-gray-600 mb-4 leading-relaxed">
              Whether you need internal doors hanging, new skirting boards fitting, or want to transform a room with wall panelling, I deliver quality workmanship with attention to detail. Every job comes with a 12-month guarantee.
            </p>
            <div className="flex flex-wrap gap-4 mt-6">
              <div className="flex items-center gap-2 text-[#2D5A4A]">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Free Quotes</span>
              </div>
              <div className="flex items-center gap-2 text-[#2D5A4A]">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">No VAT</span>
              </div>
              <div className="flex items-center gap-2 text-[#2D5A4A]">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Same Week Availability</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#3A3A3A]">
              Carpentry Services in Sale
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
              Full range of carpentry services for homes across Sale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="group border-0 shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#2D5A4A]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#C9A227]/20 transition-colors">
                    <service.icon className="h-7 w-7 text-[#2D5A4A] group-hover:text-[#C9A227] transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#3A3A3A] mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <Link to={service.link}>
                    <Button variant="outline" size="sm" className="w-full border-[#2D5A4A] text-[#2D5A4A] hover:bg-[#2D5A4A] hover:text-white">
                      Learn More
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#3A3A3A] mb-6">
            Areas Covered Near Sale
          </h2>
          <p className="text-gray-600 mb-6">
            I provide carpentry services throughout Sale and the surrounding areas:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {areas.map((area, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-[#f8f7f5] px-4 py-3 rounded-lg"
              >
                <MapPin className="h-4 w-4 text-[#C9A227]" />
                <span className="text-[#3A3A3A] font-medium">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-[#2D5A4A]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Need a Carpenter in Sale?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Get in touch for a free, no-obligation quote. I'll get back to you quickly to discuss your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold h-12 px-8"
                data-testid="sale-cta-quote-btn"
              >
                Get a Free Quote
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:07300405045">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 font-bold h-12"
              >
                <Phone className="mr-2 h-5 w-5" />
                07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalePage;
